import {Component, ViewChild, ChangeDetectorRef, OnInit, OnDestroy} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { HttpClient, HttpResponse, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { interval, of } from 'rxjs';
import * as _ from 'lodash';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { enGbLocale } from 'ngx-bootstrap/locale';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';

defineLocale('en-gb', enGbLocale);

import { Config } from 'shared/config/env.config';
import { BrandService } from 'shared/brand/brand.service';
import { UserService } from 'shared/user/user.service';
import { User } from 'shared/user/user';
import { PermissionsService } from 'shared/permissions/permissions.service';
import { SessionService } from 'shared/session/session.service';
import { TermsService } from 'shared/terms/terms.service';
import { GoogleAnalyticsEventsService } from 'shared/google-analytics/google-analytics-events.service';
import { SubscriptionService } from 'shared/subscription/subscription.service';
import { SideDrawerService } from 'shared/sidedrawer/sidedrawer.service';
import { ScorecardService } from 'shared/scorecard/scorecard.service';
import { TermsWidgetComponent } from 'shared/terms/terms-widget/terms-widget.component';

import { easings } from 'utils/easings';
import { pendoInitialize } from 'utils/pendo-analytics';

import './operators';
import { AppRoutingService } from './app-routing.service';
import {catchError, skipWhile} from 'rxjs/operators';
import {SearchPageComponent} from 'shared/search-page/search-page.component';
import {ProductPanelComponent} from './product-panel/product-panel.component';
import { UpdateService } from 'shared/update/update.service';
import { CheckForUpdateService } from 'shared/update/check-for-update.service';
import { VersionModalComponent } from 'shared/modal/version-modal/version-modal.component';


declare let ga: Function;

/**
 * This class represents the main application component.
 */
@Component({
  moduleId: module.id,
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  providers: [SideDrawerService],
})
export class AppComponent implements OnInit {
  router: Router;
  brandService: BrandService;
  userService: UserService;
  permissionsService: PermissionsService;
  sessionService: SessionService;
  user: User;
  subscription: any;


  // settings for pages
  isSidedrawerDisabled: boolean = false;
  // removes sidedrawer from dom via *ngIf
  showSidedrawer: boolean = true;
  // in case if we just need to hide sidedrawer via [class.hidden]
  // without changing page content margin and removing sidedrawer from dom
  // can be used as alternative to showSidedrawer, when showSidedrawer = true
  isSidedrawerHidden: boolean = false;
  pageContentLeftMargin: number;

  @ViewChild(TermsWidgetComponent, {static: false})
  private termsWidget: TermsWidgetComponent | void;

  @ViewChild(VersionModalComponent, {static: false})
  versionModal: VersionModalComponent;

  constructor(
    router: Router,
    appRouting: AppRoutingService,
    brandService: BrandService,
    userService: UserService,
    permissionsService: PermissionsService,
    sessionService: SessionService,
    googlAnalyticsEventsService: GoogleAnalyticsEventsService,
    protected subscriptionService: SubscriptionService,
    protected termsService: TermsService,
    protected http: HttpClient,
    private ref: ChangeDetectorRef,
    private sidedrawerService: SideDrawerService,
    private scorecardService: ScorecardService,
    private updateService: UpdateService,
    private checkForUpdateService: CheckForUpdateService,
    private localeService: BsLocaleService,
  ) {
    this.localeService.use('en-gb');
    this.setNgPageScrollConfig();
    this.router = router;
    this.brandService = brandService;
    this.userService = userService;
    this.permissionsService = permissionsService;
    this.sessionService = sessionService;
    this.sessionService.generateSessionId();
  }

  get contentMarginLeft() {
    return this.showSidedrawer ? `${this.pageContentLeftMargin}px` : '';
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnInit() {

    if (Config.PENDO_ANLYTICS_ID && this.brandService.getStoredBrand()) {
      this.http.get(Config.API + '/user')
          .pipe(
              catchError((err) => {
                // If an error occurs here we just let it go through and don't initialize pendo
                return of(false);
            })
          )

          .subscribe( (user: any) => {
            if (_.has(user, 'id')) {
              if (Config.PENDO_ANLYTICS_ID) { pendoInitialize(user, this.brandService.getStoredBrand(), Config.PENDO_ANLYTICS_ID); }
            }
      });
    }

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {

        /*
         * Run the Google Analytics for the App
         * We set the Brand Relationship Id as the first item in the
         * url for tracking purposes.
         */
        if (Config.GOOGLE_ANALYTICS_ID) {
          let page = '';

          const brandRelationshipId = this.brandService.getBrand();

          if (brandRelationshipId) {
              page = `${this.brandService.getBrand()}${event.urlAfterRedirects}`;
          } else {
              page = event.urlAfterRedirects;
          }

          ga('set', 'page', page);
          if (this.user) {
            ga('set', 'userId', this.user.id);
          }
          ga('send', 'pageview');

        }
      }
    });

    this.subscription = this.brandService.brandChange
      .subscribe(brand => {
        /*
         * This will cause the scription to reload for the selected brand.
         */
        this.subscriptionService.clearStoredSubscription();
        this.subscriptionService.clearStoredAccountBrand();

        if (Config.PENDO_ANLYTICS_ID) {
          this.userService.getUser()
            .subscribe( (user) => {
                if (Config.PENDO_ANLYTICS_ID) { pendoInitialize(user, brand, Config.PENDO_ANLYTICS_ID); }
            });
        }

        // clear scorecards session data
        this.scorecardService.clearData();

      });

    // listen to call accept terms - show terms modal
    this.termsService.fireAccept
      .subscribe(() => {
        if (this.termsWidget) {
          this.termsWidget.show();
        }
      });

    // listen to sidedrawer changes
    this.pageContentLeftMargin = this.sidedrawerService.getWidth();
    this.sidedrawerService.widthChange
        .pipe(skipWhile(() => !this.showSidedrawer))
      .subscribe((sidedrawerWidth: any) => {
        this.pageContentLeftMargin = sidedrawerWidth;
      });

    this.updateService.getAvailable()
      .subscribe(event => {
        this.updateService.setIsUpdateDelayed(true);
        this.startUpdateCheck();
        if (this.versionModal) {
          this.versionModal.show();
        }
      });
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  public setNgPageScrollConfig() {
  }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  onActivate(component: any) {
    this.isSidedrawerDisabled = !!component.isSidedrawerDisabled;
    this.showSidedrawer = !!component.showSidedrawer;
    this.isSidedrawerHidden = !!component.isSidedrawerHidden;
    this.ref.markForCheck();
  }

  startUpdateCheck() {
    interval(5 * 60 * 1000)   // every 5 minutes
      .takeWhile(() => this.updateService.isUpdateDelayed)
      .subscribe(() => {
        if (this.versionModal) {
          this.versionModal.show();
        }
      });
  }
}
